import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgentWorkspace({
  projectId,
  agentRole,
  agentLabel,
  agentEmoji,
  agentDescription,
  iteration,
  existingOutput,
  onOutputGenerated
}) {
  const [context, setContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState(existingOutput?.content || '');

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/agents/generate`, {
        project_id: projectId,
        agent_role: agentRole,
        context: context || `Genereer output voor deze AI-app. Iteration: ${iteration}`,
        iteration: iteration,
        model: 'gpt-4o'
      });
      
      setOutput(response.data.content);
      toast.success('Output gegenereerd!');
      onOutputGenerated();
    } catch (error) {
      console.error('Error generating output:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('OpenAI')) {
        toast.error('OpenAI API key niet geconfigureerd. Ga naar instellingen.');
      } else {
        toast.error('Fout bij genereren output');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Agent Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="text-4xl">{agentEmoji}</div>
            <div>
              <CardTitle>{agentLabel}</CardTitle>
              <CardDescription>{agentDescription}</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Context & Instructies</CardTitle>
          <CardDescription>
            Geef extra context of instructies voor deze AI agent (optioneel)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="context">Context</Label>
            <Textarea
              id="context"
              placeholder="Bijv: Focus op B2B SaaS markt, gebruik Nederlandse taal, etc."
              rows={3}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              data-testid={`agent-context-input-${agentRole}`}
            />
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full"
            data-testid={`generate-output-btn-${agentRole}`}
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Genereren...
              </>
            ) : output ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Regenereer Output
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Genereer Output
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Output Section */}
      {output && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Generated Output</CardTitle>
            <CardDescription>
              AI-gegenereerde output van {agentLabel}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="prose prose-sm max-w-none bg-muted rounded-lg p-6"
              data-testid={`agent-output-${agentRole}`}
            >
              <ReactMarkdown>{output}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}